//third-party
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import { At, KeyFill } from "react-bootstrap-icons";
import { Alert, Card, Form } from "react-bootstrap";

//spotsync
import { Api } from "./Data/Api";
import { Input } from "./Controls/Input";
import { Button } from "./Controls/Button";
import { CenteredPage } from "./Controls/CenteredPage";
import { HeaderText } from "./Controls/HeaderText";
import { useAtom } from "jotai";
import { authAtom, redirectAtom } from "./Data/Atoms";
import { parseStaff } from "./Data/ApiParse";
import { Staff } from "./Data/ApiTypes";
import { toast } from "react-toastify";

interface ResetPasswordForm {
    emailAddress:    string;
    password:        string;
    confirmPassword: string;
}

const validatePassword = (password: string) => {
    if (password.length < 8) {
        return "Password must be at least 8 characters long";
    }
    if (!/[A-Z]/.test(password)) {
        return "Password must contain at least one uppercase letter";
    }
    if (!/[a-z]/.test(password)) {
        return "Password must contain at least one lowercase letter";
    }
    if (!/[0-9]/.test(password)) {
        return "Password must contain at least one number";
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        return "Password must contain at least one special character (!@#$%^&*(),.?\":{}|<>)";
    }
    return true;
};

export const ResetPasswordPage = () => {
    const nav = useNavigate();
    const [searchParams, _] = useSearchParams();
    const resetCode = searchParams.get( 'code' )!;
    const [ auth,     setAuth     ] = useAtom( authAtom );
    const [ msg,      setMsg      ] = useState<string>();
    const [ redirect, setRedirect ] = useAtom( redirectAtom );
    const [isLoading, setIsLoading] = useState(false);
    const staff = useLoaderData() as Staff;

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<ResetPasswordForm>( { defaultValues: { emailAddress: staff.emailAddress } });

    function submit( data: ResetPasswordForm ) {
        setIsLoading( true );
        Api.resetPassword( {
            resetPasswordCode: resetCode!,
            newPassword:       data.password
        } ).then( res => {
            if( res.isOk ) {
                toast.success("Password reset successfully.");
                const parker = res.value;
                setAuth( { isLoggedIn: false } );

                if( redirect !== undefined ) {
                    setRedirect( undefined );
                    nav( `/login?redirect=${encodeURIComponent(redirect)}` );
                    return;
                }
                nav( "/login" );
                return;
            }
            setMsg( res.error );
        } ).finally(() => {
            setIsLoading( false );
        } );
    }

    return <CenteredPage style={{ maxWidth: "30em" }}>
        <Card className="w-100">
            <Form onSubmit={handleSubmit( submit )}>
                <Card.Header>
                    <HeaderText>Reset My Password</HeaderText>
                </Card.Header>
                <Card.Body>
                    <div className="text-secondary mb-3">
                        Reset your password below. Your password must:
                        <ul className="mt-2 mb-3" style={{ fontSize: "0.9rem" }}>
                            <li>Be at least 8 characters long</li>
                            <li>Contain at least one uppercase letter</li>
                            <li>Contain at least one lowercase letter</li>
                            <li>Contain at least one number</li>
                            <li>Contain at least one special character (!@#$%^&*(),.?&quot;:{}|&lt;&gt;)</li>
                        </ul>
                    </div>
                    <Input
                        type="email"
                        readOnly
                        label="Email Address"
                        icon={<At className="fs-5" />}
                        placeholder="Enter your email address"
                        error={errors.emailAddress}
                        {...register( "emailAddress", { required: "Email Address is required" } )}
                    />
                    <Input 
                        type="password"
                        label="New Password"
                        icon={<KeyFill className="fs-5" />}
                        error={errors.password}
                        {...register( "password", { 
                            required: "Password is required",
                            validate: validatePassword
                        } )}
                    />
                    <Input 
                        type="password"
                        label="Confirm Password"
                        icon={<KeyFill className="fs-5" />}
                        error={errors.confirmPassword}
                        {...register( "confirmPassword", {
                            required: "Please confirm your password",
                            validate: value => value === watch( 'password' ) || "Passwords do not match"
                        } )}
                    />
                    {msg && <Alert variant="secondary">{msg}</Alert>}
                </Card.Body>
                <Card.Footer>
                    <div className="d-flex flex-row justify-content-end">
                        <Button type="submit" loading={isLoading} className="w-50 btn-main">
                            Reset Password
                        </Button>
                    </div>
                </Card.Footer>
            </Form>
        </Card>
    </CenteredPage>;
};